import { notification } from "antd";
import cuid from "cuid";
import { filter, find, get, isArray, isNil, last, sortBy } from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { HiOutlineExclamationCircle } from "react-icons/hi";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { useLazyQuery, useMutation } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import format from "string-template";
import { GENERATE_QUOTE_DOCUMENTS } from "utils/api/graphql/mutations/attachment";
import { BUILD_GENERAL_COSTS } from "utils/api/graphql/mutations/costs";
import { DOWNLOAD_SINGLE_DOCUMENT } from "utils/api/graphql/mutations/download-document";
import { CREATE_LINK } from "utils/api/graphql/mutations/link";
import {
  DUPLICATE_PROJECT,
  UPDATE_PROJECT,
} from "utils/api/graphql/mutations/projects";
import { SEND_B2C_QUOTE } from "utils/api/graphql/mutations/quotes";
import { CREATE_SUBSCRIPTION } from "utils/api/graphql/mutations/subscription";
import { GENERAL_COSTS } from "utils/api/graphql/queries/general-cost";
import { INSURANCE_PLANS } from "utils/api/graphql/queries/insurance-plans";
import { OFFER } from "utils/api/graphql/queries/offers";
import {
  ACTION_TYPES,
  DATE_FORMAT,
  INSURANCE_PLAN_PARAMS_RULES,
  JOB_LIST,
  KEYS,
  PROCESS_TYPES,
  SCREENS,
  TEMPLATES,
  TEMPLATE_TYPES,
} from "utils/constants";
import { getMinDeltaStart } from "utils/helpers/array";
import { generateSQLCondition } from "utils/helpers/graphql";
import { filterByConditions } from "../../../..";
import Loader from "../Components/Loader";
import Navbar from "../Components/Navbar/Navbar";
import View from "./View";

const findStatus = (data, template) => {
  return find(data, ({ template: _ }) => _ === template);
};

const Products = ({
  processId,
  tracer,
  project,
  onNext,
  onBack,
  currentStatus,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [costsLoading, setCostsLoading] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [products, setProducts] = useState([]);
  const [buildGeneralCosts] = useMutation(BUILD_GENERAL_COSTS);
  const [updateProject] = useMutation(UPDATE_PROJECT);
  const [addSubscription] = useMutation(CREATE_SUBSCRIPTION);
  const [generateQuoteDocuments] = useMutation(GENERATE_QUOTE_DOCUMENTS);
  const [sendQuoteMutation] = useMutation(SEND_B2C_QUOTE);
  const [addLink] = useMutation(CREATE_LINK);

  const [generalCosts, { data }] = useLazyQuery(GENERAL_COSTS);
  const [getOffer] = useLazyQuery(OFFER);
  const [getDeltaStart] = useLazyQuery(INSURANCE_PLANS, {
    fetchPolicy: "no-cache",
    variables: {
      where: {
        isStandalone: true,
      },
    },
  });
  const { t } = useTranslation(NAME_SPACES.PUBLIC.PROCESSES);
  const TRANSLATION = useMemo(() => t("DETAIL", { returnObjects: true }), [t]);
  const [downloadSingleDocument] = useMutation(DOWNLOAD_SINGLE_DOCUMENT);
  const [duplicateProject] = useMutation(DUPLICATE_PROJECT);

  const projectFields = useMemo(() => get(project, "fields", {}), [project]);
  const projectContact = useMemo(() => get(project, "contact", {}), [project]);
  const projectStatuses = useMemo(
    () => get(project, "statuses", []),
    [project],
  );

  const duplicateProjectOnLocked = useCallback(() => {
    const { insurancePlans, currentSubscription, nextQuoteStatus, ...fields } =
      projectFields;

    const Template =
      TEMPLATES[PROCESS_TYPES.B2C].THEMES[TEMPLATE_TYPES.RADIANCE_COSA]
        .PRODUCTS;
    const processStatuses = get(tracer, "flow.process.processStatuses", []);
    const status = findStatus(processStatuses, Template);
    const actionsToExecute = filterByConditions(
      get(status, "actions", []),
      project,
    );
    const syncActions = actionsToExecute
      .filter(({ type }) => type === ACTION_TYPES.SYNC_PROCESSES)
      .map(({ args: { status } }) => ({
        id: cuid(),
        status: { id: status },
      }));
    duplicateProject({
      variables: {
        where: { id: project.id },
        data: {
          statuses: [{ id: cuid(), status: { id: status.id } }, ...syncActions],
          fields,
        },
      },
      onCompleted: ({ duplicateProject }) => {
        localStorage.setItem(KEYS.PROJECT_ID, duplicateProject?.id);
        navigate(
          `${SCREENS.PRIVATE.GUEST.PROJECTS.DETAIL.path}/${duplicateProject?.id}`,
          {
            state: {
              process: get(omitDeep(tracer, "__typename"), "flow.process"),
              tracer: omitDeep(tracer, "__typename"),
            },
          },
        );
      },
    });
  }, [projectFields, tracer, project, duplicateProject, navigate]);

  const checkUpsellsAndCds = useCallback((offerInsurancePlans, profession) => {
    const hasUpsells = offerInsurancePlans.some(({ weight }) => weight === 1);
    const hasCds =
      [
        JOB_LIST.HOSPITAL_CIVIL_SERVICE,
        JOB_LIST.HOSPITAL_PRACTITIONER,
      ].includes(profession) &&
      offerInsurancePlans.some(({ weight }) => weight === 2);
    return { hasUpsells, hasCds };
  }, []);

  const getCurrentStatus = useCallback(() => {
    const filteredStatuses = filter(
      projectStatuses,
      ({ status }) => status.process.id === processId,
    );
    const sortedStatuses = sortBy(filteredStatuses, "createdDate");
    return get(last(sortedStatuses), "status");
  }, [projectStatuses, processId]);

  const handleQuoteGeneration = useCallback(
    (subscriptionId, insurancePlanId, insurancePlan, possibleStatus) => {
      generateQuoteDocuments({
        variables: {
          data: {
            project: { id: project.id },
            insurancePlans: [{ id: insurancePlanId }],
          },
        },
        onCompleted: () => {
          const source = localStorage.getItem(KEYS.SOURCE);
          addLink({
            variables: {
              data: {
                url: get(
                  insurancePlan,
                  "config.generatedUrl",
                  `${process.env.REACT_APP_BASE_URL}/#/public/process/init/${source}`,
                ),
                project: { id: project.id },
                subscription: { id: subscriptionId },
                fields: {
                  process: { id: processId },
                  status: { id: get(possibleStatus, "nextStatus") },
                },
              },
            },
            onCompleted: ({ addLink: linkData }) => {
              sendQuoteMutation({
                variables: {
                  data: {
                    link: linkData?.id,
                    project: { id: project.id },
                    insurancePlans: [{ id: insurancePlanId }],
                  },
                },
                onCompleted: () => {
                  notification.open({
                    message: "Devis envoyé",
                    duration: 5,
                    icon: <IoCheckmarkCircleOutline color={COLORS.C_SUCCESS} />,
                  });
                },
              });
            },
          });
        },
      });
    },
    [project.id, processId, generateQuoteDocuments, addLink, sendQuoteMutation],
  );

  const onSubmit = useCallback(
    async (insurancePlanId) => {
      try {
        const { data } = await getOffer({
          variables: {
            where: {
              AND: [
                {
                  offerInsurancePlans: {
                    insurancePlan: { id: insurancePlanId },
                  },
                },
              ],
            },
          },
        });
        const mainOfferId = get(data, "offer.id", null);

        const { data: offer } = await getOffer({
          fetchPolicy: "no-cache",
          variables: {
            where: {
              AND: [
                { id: mainOfferId },
                { offerInsurancePlans: { main: false } },
              ],
            },
          },
        });

        const offerInsurancePlans = get(offer, "offer.offerInsurancePlans", []);
        const profession = get(projectContact, "profession");
        const { hasUpsells, hasCds } = checkUpsellsAndCds(
          offerInsurancePlans,
          profession,
        );

        const existingPlans = get(projectFields, "insurancePlans", []);
        const insurancePlans = [
          ...existingPlans.filter((id) => id !== insurancePlanId),
          insurancePlanId,
        ];

        if (hasUpsells || hasCds) {
          return onNext({
            payload: {
              fields: {
                ...projectFields,
                selectedToCompare: insurancePlans,
                lastSelected: [insurancePlanId],
                hasUpsells,
                hasCds,
              },
            },
          });
        }

        const status = getCurrentStatus();
        const { possibleStatuses } = status;
        const [possibleStatus] = filterByConditions(
          sortBy(possibleStatuses, "order"),
          {
            ...project,
            status,
          },
        );

        addSubscription({
          variables: {
            data: {
              id: cuid(),
              project: { id: project.id },
              insurancePlan: { id: insurancePlanId },
            },
          },
          onCompleted: ({
            addSubscription: { id: subscriptionId, insurancePlan },
          }) => {
            onNext({
              payload: {
                locked: true,
                visible: true,
                fields: {
                  ...projectFields,
                  insurancePlans,
                  selectedToCompare: insurancePlans,
                  lastSelected: [insurancePlanId],
                  ...(!isNil(possibleStatus?.nextStatus) && {
                    nextQuoteStatus: possibleStatus.nextStatus,
                  }),
                  currentSubscription: subscriptionId,
                  hasUpsells,
                  hasCds,
                },
              },
              onCompleted: () => {
                handleQuoteGeneration(
                  subscriptionId,
                  insurancePlanId,
                  insurancePlan,
                  possibleStatus,
                );
              },
            });
          },
        });
      } catch (error) {
        console.error("Error submitting insurance plan:", error);
        notification.open({
          message: "Une erreur est survenue",
          description: error.message,
          type: "error",
          duration: 5,
        });
      }
    },
    [
      getOffer,
      projectContact,
      projectFields,
      checkUpsellsAndCds,
      getCurrentStatus,
      onNext,
      addSubscription,
      project,
      handleQuoteGeneration,
    ],
  );

  useEffect(() => {
    setCostsLoading(true);
    getDeltaStart({
      onCompleted: ({ insurancePlans: { data: insurancePlans } }) => {
        const deltaRia = getMinDeltaStart({ insurancePlans, minValue: 30 });
        const deltaStart = getMinDeltaStart({
          insurancePlans,
          minValue: 1,
          key: INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
        });
        const deltaS = project.ria ? deltaRia : deltaStart;
        if (project?.expired) {
          notification.open({
            message: format(TRANSLATION.EXPIRATION.TITLE, {
              issuanceDate: moment().add(deltaS, "days").format(DATE_FORMAT),
            }),
            description: TRANSLATION.EXPIRATION.DESCRIPTION,
            duration: 5,
            icon: <HiOutlineExclamationCircle size={32} />,
            type: "error",
          });
        }
        const issuanceDate = moment().add(deltaS, "days");

        updateProject({
          variables: {
            where: { id: project.id },
            data: {
              expired: false,
              contract: {
                ...project.contract,
                issuanceDate,
              },
            },
          },
          onCompleted: () => {
            const adherentAge = moment(
              get(projectContact, "user.birthDate"),
            ).startOf("year");
            const profession = get(projectContact, "profession");

            const age = moment().diff(adherentAge, "years");

            const sqlConditions = [
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.MIN_AGE,
                "r.value IS NULL OR CAST(r.value AS NUMERIC) <= :value",
                age,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.MAX_AGE,
                "r.value IS NULL OR CAST(r.value AS NUMERIC) >= :value",
                age,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.PROFESSION,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${profession}'`,
              ),
              generateSQLCondition(
                INSURANCE_PLAN_PARAMS_RULES.PROCESSES,
                `r.value IS NULL OR r.value @> to_jsonb(ARRAY[:value])::jsonb OR jsonb_array_length(r.value) = 0`,
                `'${processId}'`,
              ),
            ];

            const insurancePlanFilter = {
              where: {
                isStandalone: true,
              },
              raw: sqlConditions,
              custom: {
                AND: [
                  {
                    OR: [
                      { startDate: null },
                      {
                        startDate: issuanceDate,
                        operation: "<=",
                      },
                    ],
                  },
                  {
                    OR: [
                      { endDate: null },
                      {
                        endDate: issuanceDate,
                        operation: ">=",
                      },
                    ],
                  },
                ],
              },
            };
            buildGeneralCosts({
              variables: {
                data: {
                  project: { id: project.id },
                  insurancePlanFilter,
                },
              },
              onCompleted: ({ buildGeneralCosts }) => {
                const ids = buildGeneralCosts
                  .filter(({ cost }) => cost)
                  .map(({ id }) => id);
                generalCosts({
                  fetchPolicy: "no-cache",
                  variables: {
                    isIn: { id: ids },
                  },
                  onCompleted: ({ generalCosts }) => {
                    const productsData = omitDeep(
                      get(generalCosts, "data", []),
                      "__typename",
                    );

                    const recommendedIndex = productsData.findIndex(
                      (item) => item.recommended,
                    );
                    let finalProducts = productsData;
                    if (recommendedIndex !== -1) {
                      const recommendedProduct = productsData[recommendedIndex];
                      const withoutRecommended = productsData.filter(
                        (_, i) => i !== recommendedIndex,
                      );
                      finalProducts = [
                        ...withoutRecommended.slice(0, 1),
                        recommendedProduct,
                        ...withoutRecommended.slice(1),
                      ];
                    }

                    setProducts(finalProducts);
                    setLoading(false);
                    setCostsLoading(false);
                  },
                });
              },
            });
          },
        });
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetch]);

  const refetchCosts = useCallback(
    ({ contact }) => {
      setCostsLoading(true);
      updateProject({
        variables: {
          where: { id: project.id },
          data: {
            contact,
          },
        },
        onCompleted: () => setRefetch((prev) => !prev),
      });
    },
    [project.id, updateProject],
  );
  if (loading || !isArray(products)) return <Loader />;
  return (
    <>
      <Navbar
        project={project}
        progress={currentStatus?.progress}
        tracer={tracer}
      />
      <View
        products={products}
        count={get(data, "generalCosts.count")}
        onBack={onBack}
        onSubmit={onSubmit}
        disabledProducts={get(projectFields, "insurancePlans", [])}
        contact={projectContact}
        refetchCosts={refetchCosts}
        downloadSingleDocument={downloadSingleDocument}
        locked={project.locked}
        duplicateProject={duplicateProjectOnLocked}
        costsLoading={costsLoading}
      />
    </>
  );
};

export default Products;
